/**
 * The interface's icons, drawn rather than typed.
 *
 * These were unicode glyphs (◧ ▤ ▦ ◎) until the webfont turned out to be
 * blocked by the CSP — which left them at the mercy of whatever the device
 * happened to have. Android in particular renders the geometric block
 * characters inconsistently, and some fall back to tofu. Paths render the same
 * everywhere and cost nothing to download.
 *
 * One stroke weight, round joins, 24-unit grid. `currentColor` throughout so an
 * icon inherits whatever the surrounding text is doing, including the active
 * and muted navigation states.
 */

export type IconName =
  | 'home'
  | 'posts'
  | 'analytics'
  | 'calendar'
  | 'accounts'
  | 'media'
  | 'settings'
  | 'plus'
  | 'upload'
  | 'menu'
  | 'close'
  | 'check'
  | 'alert'
  | 'cross'
  | 'dot'
  | 'dash'
  | 'collapse'
  | 'expand'
  | 'chevron'
  | 'signout'
  | 'search'
  | 'trash'
  | 'mail'
  | 'lock'
  // The three media kinds the library files things under.
  | 'video'
  | 'image'
  | 'audio'
  | 'folder'
  | 'layers';

const paths: Record<IconName, React.ReactNode> = {
  home: (
    <>
      <path d="M3.5 10.6 12 3.8l8.5 6.8" />
      <path d="M5.9 9.6V20h12.2V9.6" />
    </>
  ),
  posts: (
    <>
      <path d="M4 6.5h16" />
      <path d="M4 12h16" />
      <path d="M4 17.5h10" />
    </>
  ),
  // Three rising bars. Drawn as strokes like every other icon here rather than
  // filled rects, so it keeps the same weight as its neighbours in the rail.
  analytics: (
    <>
      <path d="M6 19V13" />
      <path d="M12 19V8" />
      <path d="M18 19v-9" />
      <path d="M3.5 19.5h17" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.2" y="5" width="17.6" height="15.8" rx="2.2" />
      <path d="M8.2 3.2v3.6" />
      <path d="M15.8 3.2v3.6" />
      <path d="M3.2 10.4h17.6" />
    </>
  ),
  accounts: (
    <>
      <circle cx="12" cy="8.2" r="3.6" />
      <path d="M4.8 20.2c0-3.7 3.2-5.6 7.2-5.6s7.2 1.9 7.2 5.6" />
    </>
  ),
  media: (
    <>
      <rect x="3.2" y="4.4" width="17.6" height="15.2" rx="2.2" />
      <path d="M10.2 9.4 15 12l-4.8 2.6z" />
    </>
  ),
  folder: (
    <path d="M3.4 6.6A1.6 1.6 0 0 1 5 5h3.7l1.9 2.1h8.4A1.6 1.6 0 0 1 20.6 8.7v8.7A1.6 1.6 0 0 1 19 19H5a1.6 1.6 0 0 1-1.6-1.6z" />
  ),
  /** "Everything", as four tiles — the same shape the grid view uses. */
  layers: (
    <>
      <rect x="3.6" y="3.6" width="7" height="7" rx="1.6" />
      <rect x="13.4" y="3.6" width="7" height="7" rx="1.6" />
      <rect x="3.6" y="13.4" width="7" height="7" rx="1.6" />
      <rect x="13.4" y="13.4" width="7" height="7" rx="1.6" />
    </>
  ),
  // A film frame: the strip and the frame inside it, which reads at 16px
  // where a clapperboard does not.
  video: (
    <>
      <rect x="2.6" y="6" width="13" height="12" rx="2" />
      <path d="m15.6 10.4 5.8-3.2v9.6l-5.8-3.2z" />
    </>
  ),
  image: (
    <>
      <rect x="3.2" y="4.4" width="17.6" height="15.2" rx="2.2" />
      <circle cx="8.6" cy="9.6" r="1.6" />
      <path d="m4 17 4.8-4.4 4.2 3.6 3-2.4 4.8 3.6" />
    </>
  ),
  // A level meter rather than a note: it says "sound" without implying the
  // file is music, which most of these will not be.
  audio: (
    <>
      <path d="M4 14.4V9.6M8.4 18V6M12.8 15.6v-7.2M17.2 19.2V4.8M21.2 13.2v-2.4" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7.4h7.2" />
      <path d="M16.4 7.4H20" />
      <path d="M4 16.6h3.6" />
      <path d="M12.8 16.6H20" />
      <circle cx="13.8" cy="7.4" r="2.4" />
      <circle cx="10.2" cy="16.6" r="2.4" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5.4v13.2" />
      <path d="M5.4 12h13.2" />
    </>
  ),
  // A tray with an arrow going into it. The Upload button has always been the
  // one primary action on the Media page wearing no glyph, because this did
  // not exist and `plus` was never quite the right word for it.
  upload: (
    <>
      <path d="M12 16V4.8" />
      <path d="m7.6 9.2 4.4-4.4 4.4 4.4" />
      <path d="M4.8 15.2v2.6a1.6 1.6 0 0 0 1.6 1.6h11.2a1.6 1.6 0 0 0 1.6-1.6v-2.6" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  close: (
    <>
      <path d="M6.2 6.2 17.8 17.8" />
      <path d="M17.8 6.2 6.2 17.8" />
    </>
  ),
  check: <path d="m5 12.4 4.6 4.6L19 7.6" />,
  alert: (
    <>
      <path d="M12 4.6 21.2 20H2.8z" />
      <path d="M12 10.4v4" />
      <path d="M12 17.2h.01" />
    </>
  ),
  cross: (
    <>
      <path d="M6.6 6.6 17.4 17.4" />
      <path d="M17.4 6.6 6.6 17.4" />
    </>
  ),
  dot: <circle cx="12" cy="12" r="4.6" fill="currentColor" stroke="none" />,
  dash: <path d="M7.5 12h9" />,
  // The rail's own control: a panel edge with the direction it will move.
  collapse: (
    <>
      <rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.2" />
      <path d="M9.6 4.6v14.8" />
      <path d="M16.4 9.6 13.8 12l2.6 2.4" />
    </>
  ),
  /* A disclosure arrow, pointing right when closed. Rotated by the caller
     rather than swapped for a second glyph, so the turn is the animation. */
  chevron: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  expand: (
    <>
      <rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.2" />
      <path d="M9.6 4.6v14.8" />
      <path d="M13.8 9.6 16.4 12l-2.6 2.4" />
    </>
  ),
  signout: (
    <>
      <path d="M10.2 4.8H6.4a1.8 1.8 0 0 0-1.8 1.8v10.8a1.8 1.8 0 0 0 1.8 1.8h3.8" />
      <path d="M15.4 8.4 19 12l-3.6 3.6" />
      <path d="M19 12H9.6" />
    </>
  ),
  mail: (
    <>
      <rect x="3.2" y="5.4" width="17.6" height="13.2" rx="2.2" />
      <path d="m3.9 7 8.1 5.6L20.1 7" />
    </>
  ),
  lock: (
    <>
      <rect x="4.6" y="10.4" width="14.8" height="9.4" rx="2.2" />
      <path d="M8.2 10.4V7.9a3.8 3.8 0 0 1 7.6 0v2.5" />
    </>
  ),
  search: (
    <>
      <circle cx="10.8" cy="10.8" r="6" />
      <path d="m15.2 15.2 4 4" />
    </>
  ),
  trash: (
    <>
      <path d="M4.6 6.8h14.8" />
      <path d="M9.4 6.8V4.9h5.2v1.9" />
      <path d="M6.6 6.8 7.5 19a1.3 1.3 0 0 0 1.3 1.2h6.4a1.3 1.3 0 0 0 1.3-1.2l.9-12.2" />
    </>
  ),
};

export function Icon({
  name,
  size = 20,
  className,
}: {
  name: IconName;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      // Decorative: every icon here sits beside its own label, or on a control
      // that carries an aria-label of its own.
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...(className === undefined ? {} : { className })}
    >
      {paths[name]}
    </svg>
  );
}
